import { isNil } from 'lodash';
import React from 'react';
import { GridCellProps } from 'react-virtualized';
import {
  useTableColumnSortState,
  useTableColumnWidthState,
} from '../../../MultiGridTable';
import { ITableSettings } from '../../interfaces';
import * as ChartDataTableUI from '../ChartDataTableUI';
import { useChartDataTable } from '../useChartDataTable';
import { TableSortIcon } from '../../../Icon';
import { Order } from '../../../../utils/apollo/graphqlSchema';
import KubitTooltip from '../../../KubitTooltip';

interface IProps {
  gridCellProps: GridCellProps;
  dataTableData: ReturnType<typeof useChartDataTable>;
  columnWidthsData: ReturnType<typeof useTableColumnWidthState>;
  columnSortStateData: ReturnType<typeof useTableColumnSortState>;
  tableSettings: ITableSettings;
}

const ComparisonHeaderRenderer = ({
  gridCellProps,
  dataTableData,
  columnWidthsData,
  columnSortStateData,
  tableSettings,
}: IProps) => {
  const { breakdowns } = dataTableData;
  const { columnIndex, rowIndex, key, style } = gridCellProps;
  const { columnWidths, handleMouseDown } = columnWidthsData;
  const { sortColumnIndex, sortDirection, onSort } = columnSortStateData;
  if (columnIndex < breakdowns.length && columnIndex === 0) {
    const width = columnWidths
      .slice(0, breakdowns.length)
      .reduce((a: number, b: number) => a + b, 0);
    return (
      <ChartDataTableUI.HeaderCell
        key={key}
        style={{
          ...style,
          width,
        }}
        $columnIndex={columnIndex}
        $rowIndex={rowIndex}
        $isEmptyCell
      />
    );
  }

  const colIndex = columnIndex - breakdowns.length;
  const numComparisonColumns = tableSettings.showPercentDelta ? 3 : 2;
  const isColumnSorted = sortColumnIndex === columnIndex;

  if (colIndex >= 0) {
    if (colIndex % numComparisonColumns === 0) {
      return (
        <ChartDataTableUI.HeaderCell
          key={key}
          style={style}
          $columnIndex={columnIndex}
          $rowIndex={rowIndex}
          $isBackgroundFilled={true}
        >
          <ChartDataTableUI.SortableContainer
            onClick={() => onSort({ index: columnIndex })}
          >
            Previous
            {isColumnSorted && !isNil(sortDirection) && (
              <TableSortIcon isSortAsc={sortDirection === Order.Asc} />
            )}
          </ChartDataTableUI.SortableContainer>

          <ChartDataTableUI.ChartColumnResizeHandle
            onMouseDown={(e) => handleMouseDown(e, columnIndex)}
          />
        </ChartDataTableUI.HeaderCell>
      );
    }

    if (colIndex % numComparisonColumns === 1) {
      return (
        <ChartDataTableUI.HeaderCell
          key={key}
          style={style}
          $columnIndex={columnIndex}
          $rowIndex={rowIndex}
          $isBackgroundFilled={true}
        >
          <ChartDataTableUI.SortableContainer
            onClick={() => onSort({ index: columnIndex })}
          >
            Current
            {isColumnSorted && !isNil(sortDirection) && (
              <TableSortIcon isSortAsc={sortDirection === Order.Asc} />
            )}
          </ChartDataTableUI.SortableContainer>
          <ChartDataTableUI.ChartColumnResizeHandle
            onMouseDown={(e) => handleMouseDown(e, columnIndex)}
          />
        </ChartDataTableUI.HeaderCell>
      );
    }

    if (colIndex % numComparisonColumns === 2) {
      return (
        <ChartDataTableUI.HeaderCell
          key={key}
          style={style}
          $columnIndex={columnIndex}
          $rowIndex={rowIndex}
          $isBackgroundFilled={true}
        >
          <KubitTooltip
            content={sortDirection === Order.Asc ? 'Ascending' : 'Descending'}
            disabled={!isColumnSorted}
            placement="bottom"
          >
            <ChartDataTableUI.SortableContainer
              onClick={() => onSort({ index: columnIndex })}
            >
              Percent Delta
              {isColumnSorted && !isNil(sortDirection) && (
                <TableSortIcon isSortAsc={sortDirection === Order.Asc} />
              )}
            </ChartDataTableUI.SortableContainer>
          </KubitTooltip>
          <ChartDataTableUI.ChartColumnResizeHandle
            onMouseDown={(e) => handleMouseDown(e, columnIndex)}
          />
        </ChartDataTableUI.HeaderCell>
      );
    }
  }

  return null;
};

export default ComparisonHeaderRenderer;
